import React, { useState } from "react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { ListingFormSection } from "@/components/ListingFormSection";
import {
  CONDITIONS,
  DRIVE_TYPES,
  EURO_CLASSES,
  MONTH_OPTIONS,
  PHONE_LANG_OPTIONS,
  ROOF_TYPES,
  WARRANTY_TYPES,
} from "@/lib/listingForm";

const Check = ({ label, testid, checked, onChange, className = "" }) => (
  <label className={`flex items-center gap-2 text-sm text-slate-300 cursor-pointer ${className}`}>
    <input
      type="checkbox"
      data-testid={testid}
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="rounded border-white/20"
    />
    {label}
  </label>
);

export function ListingVehicleFields({
  form,
  set,
  setForm,
  trims = [],
  loadingTrims = false,
  onTrimSelect,
  Inp,
  Field,
}) {
  const [showPerfOverride, setShowPerfOverride] = useState(
    Boolean(form.horsepower || form.acceleration),
  );

  const toggleLang = (code) => {
    setForm((prev) => {
      const langs = prev.phone_languages || [];
      const has = langs.includes(code);
      return {
        ...prev,
        phone_languages: has ? langs.filter((x) => x !== code) : [...langs, code],
      };
    });
  };

  const trimLabels = trims.map((t) => t.label);

  return (
    <div className="md:col-span-2 space-y-6">
      <ListingFormSection
        title="Műszaki adatok"
        hint="A kivitel / VIN betöltés után sok mező automatikusan kitöltődik. A ritkább adatok a lenyílóban vannak."
        testid="listing-section-tech"
      >
        <Field label="Kivitel / trim">
          <SearchableSelect
            testid="listing-trim"
            value={form.trim_label || ""}
            onChange={(label) => {
              const t = trims.find((x) => x.label === label);
              onTrimSelect?.(t?.id || "");
            }}
            options={trimLabels}
            loading={loadingTrims}
            disabled={!form.make || !form.model}
            placeholder={trims.length ? "Opcionális…" : "Nincs katalógus-lista ehhez a modellhez"}
            emptyText="Nincs illeszkedő kivitel"
          />
        </Field>
        <Field label="Hajtás">
          <SearchableSelect testid="listing-drive" value={form.drive || ""} onChange={(v) => set("drive", v)} options={["", ...DRIVE_TYPES]} placeholder="Hajtás" />
        </Field>
        <Field label="Euro osztály">
          <SearchableSelect testid="listing-euro" value={form.euro_class || ""} onChange={(v) => set("euro_class", v)} options={["", ...EURO_CLASSES]} placeholder="Környezetvédelmi osztály" />
        </Field>
        <Field label="Műszaki vizsga érvényes">
          <div className="flex gap-2">
            <input
              data-testid="listing-tuv-year"
              type="number"
              placeholder="Év"
              value={form.tuv_valid_year ?? ""}
              onChange={(e) => set("tuv_valid_year", e.target.value ? Number(e.target.value) : null)}
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm focus:border-electric outline-none"
            />
            <SearchableSelect
              testid="listing-tuv-month"
              value={form.tuv_valid_month || ""}
              onChange={(v) => set("tuv_valid_month", v ? Number(v) : null)}
              options={[{ v: "", l: "Hó" }, ...MONTH_OPTIONS.map((m) => ({ v: m.v, l: m.l }))]}
              placeholder="Hó"
              searchable={false}
            />
          </div>
        </Field>

        <details className="md:col-span-2 group rounded-xl border border-white/10 bg-white/[0.02] open:border-white/15">
          <summary className="cursor-pointer list-none px-4 py-3 text-sm text-slate-400 hover:text-slate-200">
            További műszaki adatok (hengerűrtartalom, ajtók, teljesítmény…)
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 pb-4 pt-1 border-t border-white/10">
            <Inp label="Hengerűrtartalom (cm³)" testid="listing-cc" type="number" value={form.displacement_cm3 ?? ""} onChange={(v) => set("displacement_cm3", v ? Number(v) : null)} />
            <Inp label="Teljesítmény (kW)" testid="listing-kw" type="number" value={form.power_kw ?? ""} onChange={(v) => set("power_kw", v ? Number(v) : null)} />
            <Inp label="Ajtók" testid="listing-doors" type="number" value={form.doors ?? ""} onChange={(v) => set("doors", v ? Number(v) : null)} />
            <Inp label="Ülések (fő)" testid="listing-seats" type="number" value={form.seats ?? ""} onChange={(v) => set("seats", v ? Number(v) : null)} />
            <Inp label="Csomagtartó (l)" testid="listing-trunk" type="number" value={form.trunk_liters ?? ""} onChange={(v) => set("trunk_liters", v ? Number(v) : null)} />
            <div className="md:col-span-2">
              <button
                type="button"
                className="text-xs text-slate-400 hover:text-electric mb-2"
                onClick={() => setShowPerfOverride((x) => !x)}
              >
                {showPerfOverride ? "LE / 0–100 elrejtése" : "LE és 0–100 megadása"}
              </button>
              {showPerfOverride ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Inp label="Teljesítmény (LE)" testid="listing-hp" type="number" value={form.horsepower ?? ""} onChange={(v) => set("horsepower", v ? Number(v) : null)} />
                  <Inp label="Gyorsulás 0–100 (mp)" testid="listing-acc" type="number" step="0.1" value={form.acceleration ?? ""} onChange={(v) => set("acceleration", v ? Number(v) : null)} />
                </div>
              ) : (form.horsepower || form.acceleration) ? (
                <p className="text-xs text-slate-500" data-testid="listing-perf-hint">
                  {form.horsepower ? `${form.horsepower} LE` : ""}
                  {form.horsepower && form.acceleration ? " · " : ""}
                  {form.acceleration ? `0–100: ${form.acceleration} mp` : ""}
                </p>
              ) : null}
            </div>
          </div>
        </details>
      </ListingFormSection>

      <ListingFormSection title="Megjelenés" testid="listing-section-appearance">
        <Check label="Metál festék" testid="listing-metallic" checked={form.color_metallic} onChange={(v) => set("color_metallic", v)} className="md:col-span-2" />
        <Field label="Tető">
          <SearchableSelect testid="listing-roof" value={form.roof_type || ""} onChange={(v) => set("roof_type", v)} options={["", ...ROOF_TYPES]} placeholder="Tető" />
        </Field>
        <Inp label="Belső szín" testid="listing-interior" value={form.interior_color || ""} onChange={(v) => set("interior_color", v)} />
      </ListingFormSection>

      <ListingFormSection
        title="Értékesítés és finanszírozás"
        hint="Az eladási ár a fenti „Ár” mezőben van. Itt az akció, garancia és hitel opciók."
        testid="listing-section-sales"
      >
        <Inp label="Akciós ár (Ft)" testid="listing-sale-price" type="number" value={form.sale_price ?? ""} onChange={(v) => set("sale_price", v ? Number(v) : null)} />
        <Field label="Garancia">
          <SearchableSelect testid="listing-warranty-type" value={form.warranty_type || ""} onChange={(v) => set("warranty_type", v)} options={WARRANTY_TYPES} placeholder="Típus" />
        </Field>
        <Inp label="Garancia eddig" testid="listing-warranty-until" value={form.warranty_until || ""} onChange={(v) => set("warranty_until", v)} placeholder="pl. 2027-06" />
        <Inp label="Garancia km-ig" testid="listing-warranty-km" type="number" value={form.warranty_km ?? ""} onChange={(v) => set("warranty_km", v ? Number(v) : null)} />
        <div className="md:col-span-2 flex flex-col gap-2">
          <Check label="ÁFA visszaigényelhető" testid="listing-vat" checked={form.vat_reclaimable} onChange={(v) => set("vat_reclaimable", v)} />
          <Check label="Autóbeszámítás lehetséges" testid="listing-trade-in" checked={form.trade_in_possible} onChange={(v) => set("trade_in_possible", v)} />
          <Check label="Azonnal elvihető" testid="listing-immediate" checked={form.immediate_available !== false} onChange={(v) => set("immediate_available", v)} />
        </div>
        <details className="md:col-span-2 rounded-xl border border-white/10 bg-white/[0.02]">
          <summary className="cursor-pointer list-none px-4 py-3 text-sm text-slate-400">Finanszírozás / bérlés (opcionális)</summary>
          <div className="px-4 pb-4 pt-1 space-y-3 border-t border-white/10">
            <Check label="Hitel / részletfizetés" testid="listing-finance" checked={form.finance_available} onChange={(v) => set("finance_available", v)} />
            {form.finance_available && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Inp label="Kezdőrészlet (Ft)" testid="listing-finance-down" type="number" value={form.finance_down_payment ?? ""} onChange={(v) => set("finance_down_payment", v ? Number(v) : null)} />
                <Inp label="Havi (Ft)" testid="listing-finance-monthly" type="number" value={form.finance_monthly ?? ""} onChange={(v) => set("finance_monthly", v ? Number(v) : null)} />
                <Inp label="Futam (hó)" testid="listing-finance-months" type="number" value={form.finance_months ?? ""} onChange={(v) => set("finance_months", v ? Number(v) : null)} />
              </div>
            )}
            <Check label="Bérlési lehetőség" testid="listing-lease" checked={form.lease_available} onChange={(v) => set("lease_available", v)} />
          </div>
        </details>
      </ListingFormSection>

      <ListingFormSection
        title="Elérhetőség a hirdetésben"
        hint="Ha üresen hagyod, a profilod telefonszáma jelenik meg. Második szám és nyelvek opcionálisak."
        testid="listing-section-contact"
      >
        <Inp label="Telefon (felülírás)" testid="listing-phone" value={form.listing_phone || ""} onChange={(v) => set("listing_phone", v)} placeholder="Profil telefon helyett" />
        <Inp label="Második telefon" testid="listing-phone-2" value={form.seller_phone_2 || ""} onChange={(v) => set("seller_phone_2", v)} />
        <div className="md:col-span-2">
          <span className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">Beszélt nyelvek</span>
          <div className="flex flex-wrap gap-2">
            {PHONE_LANG_OPTIONS.map(({ v, l }) => (
              <button
                key={v}
                type="button"
                data-testid={`listing-lang-${v}`}
                onClick={() => toggleLang(v)}
                className={`px-3 py-1.5 rounded-full text-xs border ${(form.phone_languages || []).includes(v) ? "border-electric bg-electric/15 text-electric" : "border-white/10 text-slate-400"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <Check label="E-mail megjelenítése" testid="listing-show-email" checked={form.show_email_public} onChange={(v) => set("show_email_public", v)} className="md:col-span-2" />
        {form.show_email_public && (
          <Inp label="Publikus e-mail" testid="listing-public-email" value={form.public_email || ""} onChange={(v) => set("public_email", v)} className="md:col-span-2" />
        )}
      </ListingFormSection>
    </div>
  );
}
